import { memo, useCallback, useEffect, useState } from 'react'
import { fetchAnalyticsModeHotColdNumbers } from '@AppState/analytics-mode/analytics-mode-slice'
import { RootState, useAppDispatch } from '@AppState/redux-store'
import './styles.scss'
import { useSelector } from 'react-redux'
import { DataFetchingState } from '@AppState/types'
import { LoadingAnimation } from '@Components/loading/loading'
import { HotColdNumber, HotOrCold } from './hot-cold-number/hot-cold-number'
import * as Slider from '@radix-ui/react-slider'

const ROOT_CLASS = `analytics-mode`

export const AnalyticsMode = memo(() => {
  const dispatch = useAppDispatch()

  const analyticsState = useSelector((state: RootState) => state.analyticsMode)
  const analyticsConfig = useSelector((state: RootState) => state.hostConfig.config?.analyticsPage)

  const sliderStartingValue = 50
  const [currentSliderValue, setCurrentSliderValue] = useState([sliderStartingValue])
  const [selectedSliderValue, setSelectedSliderValue] = useState(sliderStartingValue)

  useEffect(() => {
    dispatch(
      fetchAnalyticsModeHotColdNumbers({
        numDraws: sliderValueToNonLinearResult(selectedSliderValue),
      }),
    )
  }, [dispatch, selectedSliderValue])
  const ErrorMessage = () => {
    return (
      <div className={`${ROOT_CLASS}__error-message`}>
        <h1> Oops! </h1>
        <h2> Something went wrong.</h2>
        <h2> Please try again.</h2>
        <button onClick={() => {}}> Retry </button>
      </div>
    )
  }

  const findFrequency = useCallback(
    (drawValue: number) => {
      return (
        analyticsState.response!.statistics.find((x) => x.drawValue === drawValue)
          ?.percentageOfWins || 0
      )
    },
    [analyticsState],
  )

  const generateHotColdNumbers = (hotOrCold: HotOrCold) => {
    let numbers = new Array(10).fill(0, 0, 10)
    if (analyticsState.response) {
      if (hotOrCold === 'HOT') {
        numbers = [...analyticsState.response.hotNumbers].reverse()
      } else {
        numbers = analyticsState.response.coldNumbers
      }

      return numbers.map((val: number, index: number) => (
        <HotColdNumber
          key={`${hotOrCold.toLowerCase()}-number-${index}`}
          hotOrCold={hotOrCold}
          frequency={findFrequency(val)}
          selectedNumber={val}
          loading={
            analyticsState.fetchState === DataFetchingState.PENDING ||
            analyticsState.fetchState === DataFetchingState.IDLE
          }
        />
      ))
    }
  }

  const generateResults = () => {
    switch (analyticsState.fetchState) {
      case DataFetchingState.IDLE:
        return <LoadingAnimation />
      case DataFetchingState.PENDING:
      case DataFetchingState.SUCCEEDED:
        return (
          <div className={`${ROOT_CLASS}__results`}>
            <div className={`${ROOT_CLASS}__results-section`}>
              <h2>Hot Numbers</h2>
              <div className={`${ROOT_CLASS}__numbers-section`}>
                {generateHotColdNumbers('HOT')}
              </div>
            </div>
            <div className={`${ROOT_CLASS}__results-section`}>
              <h2>Cold Numbers</h2>
              <div className={`${ROOT_CLASS}__numbers-section`}>
                {generateHotColdNumbers('COLD')}
              </div>
            </div>
          </div>
        )
      case DataFetchingState.FAILED:
      default:
        return <ErrorMessage />
    }
  }

  // Convert slider's internal 0–100 value to displayed 25–100 non-linearly
  function sliderValueToNonLinearResult(val: number) {
    if (val <= 50) {
      return Math.floor(25 + (val / 50) * 25)
    } else {
      return Math.floor(50 + ((val - 50) / 50) * 50)
    }
  }

  return (
    <div className={ROOT_CLASS}>
      <div className={`${ROOT_CLASS}__header`}>
        <h1>{analyticsConfig?.pageTitle || 'Number Analytics'}</h1>
        <h2> {analyticsConfig?.pageSubtitle || 'Information for entertainment purposes only'}</h2>
      </div>
      {generateResults()}
      <div className={`${ROOT_CLASS}__slider-section`}>
        <h2>{analyticsConfig?.sliderTitle || 'Last Number of Draws'}</h2>
        <div className={`${ROOT_CLASS}__slider__container`}>
          <Slider.Root
            aria-label={'Last Number of Draws Slider'}
            className={`${ROOT_CLASS}__slider__root`}
            defaultValue={[50]}
            onValueCommit={(e) => {
              setSelectedSliderValue(e[0])
            }}
            onValueChange={(e) => {
              setCurrentSliderValue(e)
            }}
            min={0}
            max={100}>
            <Slider.Track className={`${ROOT_CLASS}__slider__track`}>
              <Slider.Range className={`${ROOT_CLASS}__slider__range`} />
            </Slider.Track>
            <Slider.Thumb
              className={`${ROOT_CLASS}__slider__thumb`}
              aria-valuetext={`${sliderValueToNonLinearResult(currentSliderValue[0])}`}>
              {currentSliderValue[0] !== selectedSliderValue && (
                <div className={`${ROOT_CLASS}__slider__value`}>
                  {sliderValueToNonLinearResult(currentSliderValue[0])}
                </div>
              )}
            </Slider.Thumb>
          </Slider.Root>
          <div className={`${ROOT_CLASS}__slider__label`}>
            <p>25</p>
            <p>50</p>
            <p>100</p>
          </div>
        </div>
      </div>
    </div>
  )
})
